// reviewTarget (0167 N3): where a "take me to what waits on me" link lands.
// Review's pos is an index into its cards (lib/reviewCards.ts), which hold the
// queue's served items in served order (views/Review.tsx, `?queue=<q>&pos=<n>`),
// so a link lands on the card holding the item, by that card's index. A link to
// an item on the shared card names the item as the fragment, and the card
// scrolls that row into view (0074).

import type { Queue } from "../hooks/useQueues";
import { reviewCards, type ReviewCard } from "./reviewCards";

export type ReviewQueue = "questions" | "decisions";

// firstPosWhere is the index of the first served item whose id `wanted`
// accepts; 0 when none does (Review's own default), an empty queue included.
export function firstPosWhere(items: readonly { id: string }[], wanted: (id: string) => boolean): number {
  const i = items.findIndex((n) => wanted(n.id));
  return i < 0 ? 0 : i;
}

// reviewQueueHref is the Review link to `queue` at `pos`.
export function reviewQueueHref(productId: string, queue: ReviewQueue, pos: number): string {
  return `/products/${productId}/review?queue=${queue}&pos=${pos}`;
}

// firstCardWhere is the index of the first card holding an item whose id `wanted`
// accepts; 0 when none does (Review's own default), no cards included. It reads
// firstPosWhere over the card ids, so "else 0" is spelled once.
export function firstCardWhere(cards: readonly ReviewCard[], wanted: (itemId: string) => boolean): number {
  const holding = new Set(cards.filter((c) => c.nodes.some((n) => wanted(n.id))).map((c) => c.id));
  return firstPosWhere(cards, (id) => holding.has(id));
}

// reviewCardHref is the link into Review for an item: the first card holding an
// item `wanted` accepts; the first card when none does, or while the queue loads.
// A link to an item on the shared card names the item as the fragment
// (`#<item id>`), and the card scrolls that row into view, when the first of the
// card's items `wanted` accepts is not its first; every other link carries none.
export function reviewCardHref(
  productId: string,
  queue: ReviewQueue,
  served: Queue | undefined,
  wanted: (itemId: string) => boolean,
): string {
  const cards = reviewCards(served);
  const pos = firstCardWhere(cards, wanted);
  const href = reviewQueueHref(productId, queue, pos);
  const card = cards[pos];
  if (card === undefined || card.nodes.length < 2) return href;
  const item = card.nodes.find((n) => wanted(n.id));
  if (item === undefined || item === card.nodes[0]) return href;
  return `${href}#${encodeURIComponent(item.id)}`;
}
